// src/components/OrderEntry/hooks/usePickingQueue.js
/*-------------------------------------------------------------------------------
  ORDER ENTRY HOOK — usePickingQueue
  ---------------------------------------------------------------------------
  Manages a picking queue of sheets and lines, allowing parts to be added
  from order entries, grouped by header characteristics.

  IMPORTANT RULES:
  - Each sheet gets a stable pickSheetNo (1..N, no reuse unless cleared)
  - PO uniqueness: append " (N)" ONLY when pickSheetNo > 1 (handled at submit/copy time)
  ---------------------------------------------------------------------------*/

// ========================================================================
// BLOCK PQ.0 — Imports
// ========================================================================
import { useCallback, useMemo, useState } from 'react';

function safe(v) {
  return String(v ?? '').trim();
}

function nextAvailablePickSheetNo(existingSheets) {
  const used = new Set(
    (existingSheets || []).map((s) => Number(s.pickSheetNo)).filter((n) => Number.isFinite(n))
  );
  let n = 1;
  while (used.has(n)) {
    n += 1;
  }
  return n;
}

// ============================================================================
// BLOCK PQ.SHEET.KEY.1 — Picking sheet identity (Excel parity)
// RULE:
// - SAME sheet if these match
// - NEW sheet if ANY differ
// ============================================================================
// NOTE (fix):
// - Include ALL special-order flags (BO/MO/DS/BL), not just BO
// - Include splitCommission because it changes submit behavior / downstream handling
function headerKeyFrom(oe) {
  return JSON.stringify({
    // Customer context (must match)
    company: safe(oe.company),
    masId: safe(oe.masId),
    shipId: safe(oe.shipId),

    // Shipping identity
    shipVia: safe(oe.shipVia),
    carrier: safe(oe.carrier),
    frtPay: safe(oe.frtPay),

    // Contract identity — ONLY contractCode matters
    contractCode: safe(oe.contractCode),

    // Flags that affect fulfillment / submit behavior
    zeroInsurance: !!oe.zeroInsurance,

    // Special order types (do NOT collapse)
    specOrderBo: !!oe.specOrderBo,
    specOrderMo: !!oe.specOrderMo,
    specOrderDs: !!oe.specOrderDs,
    specOrderBl: !!oe.specOrderBl,

    // Other flags that affect workflow
    splitCommission: !!oe.splitCommission,
    houseAccount: !!oe.houseAccount,
    internationalSale: !!oe.internationalSale,
  });
}

function uuid() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function usePickingQueue() {
  // ========================================================================
  // BLOCK PQ.1 — State
  // ========================================================================
  const [sheets, setSheets] = useState([]);

  // Optional: stable ordering for render
  const sortedSheets = useMemo(() => {
    return [...sheets].sort((a, b) => {
      const an = Number(a.pickSheetNo);
      const bn = Number(b.pickSheetNo);
      if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) {
        return an - bn;
      }
      return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
    });
  }, [sheets]);

  // ========================================================================
  // BLOCK PQ.2 — Actions
  // ========================================================================

  const addPartFromEntry = useCallback((oe) => {
    const hk = headerKeyFrom(oe);

    const header = {
      company: oe.company,
      masId: oe.masId,
      shipId: oe.shipId,

      poNumber: oe.poNumber,
      confirmTo: oe.confirmTo,
      phone: oe.phone,
      receivingLine: oe.receivingLine,

      carrier: oe.carrier,
      frtPay: oe.frtPay,
      freightMethod: oe.frtPay,
      shipVia: oe.shipVia,
      termsCode: oe.termsValue,

      frtAccount: oe.frtAccount,
      freightContract: oe.frtAccount,
      contractCode: oe.contractCode,
      contractNo: oe.contractNo,

      flags: {
        zeroInsurance: !!oe.zeroInsurance,

        // Special order types (do NOT collapse)
        specOrderBo: !!oe.specOrderBo,
        specOrderMo: !!oe.specOrderMo,
        specOrderDs: !!oe.specOrderDs,
        specOrderBl: !!oe.specOrderBl,

        splitCommission: !!oe.splitCommission,
        houseAccount: !!oe.houseAccount,
        internationalSale: !!oe.internationalSale,
      },

      // Display helpers
      etaLabel: oe.shipEtaMap?.[oe.shipVia]?.label || '',
      etaDate: oe.shipEtaMap?.[oe.shipVia]?.date || null,
    };

    const line = {
      lineId: uuid(),
      partNo: oe.partNo,
      qty: oe.qty,
      uom: oe.uom,
      price: oe.price,
      contractCode: oe.contractCode || '',

      // ---------------------------------------------------------------------
      // BLOCK PQ.LINE.SPEC.0 — Preserve per-line special-order metadata
      // Required by:
      // - OE.SUGAR.SPEC.0 buildSpecialOrderLinesForSugar(line)
      // - submit-order payload -> backend /C rows
      // ---------------------------------------------------------------------
      specialOrderType: String(oe?.specialOrderType || '').trim(),
      customerNote: String(oe?.customerNote || '').trim(),
    };

    setSheets((prev) => {
      const idx = prev.findIndex((s) => s.headerKey === hk);

      // Existing sheet: append line
      if (idx >= 0) {
        const next = [...prev];

        // Keep a fresh snapshot of sheet-level submit/header values.
        // This ensures submit payload can always rely on sheet.header
        // even after users change freight/account/terms before adding
        // additional lines to the same sheet key.
        const mergedHeader = {
          ...(next[idx]?.header || {}),
          ...header,
        };

        next[idx] = {
          ...next[idx],
          header: mergedHeader,
          lines: [...next[idx].lines, line],
        };
        return next;
      }

      // New sheet: assign a stable pickSheetNo
      const pickSheetNo = nextAvailablePickSheetNo(prev);

      return [
        ...prev,
        {
          sheetId: uuid(),
          headerKey: hk,
          pickSheetNo,
          header,
          lines: [line],
          createdAt: new Date().toISOString(),
        },
      ];
    });
  }, []);

  // ============================================================================
  // BLOCK PQ.API.RESET_FOR_CUSTOMER.0 — Clear queue when customer/address changes
  // ============================================================================
  const resetForCustomer = useCallback(() => {
    setSheets([]);
  }, []);

  const removeSheet = useCallback((sheetId) => {
    setSheets((prev) => prev.filter((s) => s.sheetId !== sheetId));
  }, []);

  const removeLine = useCallback((sheetId, lineId) => {
    setSheets((prev) =>
      prev
        .map((s) =>
          s.sheetId !== sheetId ? s : { ...s, lines: s.lines.filter((l) => l.lineId !== lineId) }
        )
        .filter((s) => s.lines.length > 0)
    );
  }, []);

  const clearAll = useCallback(() => setSheets([]), []);

  return {
    sheets: sortedSheets,
    addPartFromEntry,
    removeSheet,
    removeLine,
    clearAll,
    resetForCustomer,
  };
}
